"use client";

import { ChevronRight, Search, X } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import type { ActivityDefinition } from "@/lib/activities";
import { cn } from "@/lib/utils";
import { ActivityGlyph } from "./activity-glyph";
import {
  ACTIVITY_PICKER_FILTER_OPTIONS,
  type ActivityPickerItem,
} from "./activity-session-drawer.constants";
import type { ActivityPicker } from "./use-activity-picker";

const TILE_GRID = "grid grid-cols-3 gap-2";

function PickerSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2.5">
      <div className="flex items-baseline justify-between gap-3 px-0.5">
        <h3 className="text-[13px] font-semibold text-text-primary">{title}</h3>
        {hint ? (
          <p className="truncate text-xs text-text-tertiary">{hint}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function ActivityTile({
  item,
  onSelect,
}: {
  item: ActivityPickerItem;
  onSelect: (activityId: ActivityDefinition["id"]) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item.activity.id)}
      className="flex min-w-0 flex-col items-center gap-2 rounded-2xl border border-border bg-background px-2 pt-3.5 pb-3 text-center transition-[transform,border-color,background-color] duration-150 ease-out-strong active:scale-[0.97] dark:border-white/10 dark:bg-white/[0.04] pointer-fine:hover:border-brand/40 dark:pointer-fine:hover:border-brand/50"
    >
      <ActivityGlyph activity={item.activity} />
      <span className="block w-full min-w-0">
        <span className="line-clamp-2 block text-[13px] leading-4 font-semibold text-balance text-text-primary">
          {item.activity.name}
        </span>
        <span className="mt-0.5 block truncate text-[11px] leading-4 text-text-tertiary">
          {item.subtitle}
        </span>
      </span>
    </button>
  );
}

function RecentChip({
  item,
  onSelect,
}: {
  item: ActivityPickerItem;
  onSelect: (activityId: ActivityDefinition["id"]) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item.activity.id)}
      className="flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-background py-1 pr-3 pl-1 transition-[transform,border-color] duration-150 ease-out-strong active:scale-[0.97] dark:border-white/10 dark:bg-white/[0.04] pointer-fine:hover:border-brand/40"
    >
      <ActivityGlyph activity={item.activity} size="sm" />
      <span className="text-[13px] font-semibold text-text-primary">
        {item.activity.name}
      </span>
    </button>
  );
}

function TileGridSkeleton() {
  return (
    <div className={TILE_GRID} aria-hidden="true">
      {Array.from({ length: 9 }, (_, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
          key={index}
          className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-background px-2 pt-3.5 pb-3 dark:border-white/10 dark:bg-white/[0.04]"
        >
          <Skeleton className="size-10 rounded-xl" />
          <div className="flex w-full flex-col items-center gap-1">
            <Skeleton className="h-3.5 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-border px-6 py-10 text-center dark:border-white/10">
      <p className="text-sm font-semibold text-text-primary">{title}</p>
      <p className="mt-1 max-w-[16rem] text-xs leading-5 text-text-secondary">
        {body}
      </p>
      {action}
    </div>
  );
}

type ActivityPickerStepProps = {
  picker: ActivityPicker;
  isLoading: boolean;
  hasActivities: boolean;
  onSelectActivity: (activityId: ActivityDefinition["id"]) => void;
};

export function ActivityPickerStep({
  picker,
  isLoading,
  hasActivities,
  onSelectActivity,
}: ActivityPickerStepProps) {
  const {
    query,
    setQuery,
    filter,
    setFilter,
    normalizedQuery,
    searchedItems,
    popularItems,
    recentItems,
    matchItems,
    sessionItems,
    allItems,
  } = picker;
  const searchRef = useRef<HTMLInputElement>(null);

  // Phones open on the list: focusing here would throw the keyboard over half
  // the sheet before anyone asked to type. A mouse-and-keyboard setup gets the
  // caret straight away instead.
  useEffect(() => {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      return;
    }
    searchRef.current?.focus({ preventScroll: true });
  }, []);

  const renderTiles = (items: ActivityPickerItem[]) => (
    <div className={TILE_GRID}>
      {items.map((item) => (
        <ActivityTile
          key={item.activity.id}
          item={item}
          onSelect={onSelectActivity}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="sticky top-0 z-10 -mx-4 space-y-3 bg-surface px-4 pt-1 pb-2 dark:bg-black">
        <label className="relative block">
          <span className="sr-only">Search activities</span>
          <Search className="pointer-events-none absolute top-1/2 left-3.5 size-[18px] -translate-y-1/2 text-text-tertiary" />
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tennis, hyrox, yoga…"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            // 16px keeps iOS from zooming the page when the field takes focus.
            className="h-11 w-full rounded-xl border border-transparent bg-surface-alt pr-10 pl-10 text-base text-text-primary transition-[border-color,box-shadow] duration-150 ease-out-strong outline-none placeholder:text-text-tertiary focus:border-brand/40 focus:ring-4 focus:ring-brand/15 dark:bg-white/[0.07] [&::-webkit-search-cancel-button]:hidden"
          />
          {query ? (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                searchRef.current?.focus({ preventScroll: true });
              }}
              aria-label="Clear search"
              className="absolute top-1/2 right-2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-text-tertiary transition-colors pointer-fine:hover:text-text-primary"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </label>

        {normalizedQuery ? null : (
          <div className="-mx-4 no-scrollbar flex gap-1.5 overflow-x-auto px-4">
            {ACTIVITY_PICKER_FILTER_OPTIONS.map((option) => {
              const isActive = filter === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setFilter(option.value)}
                  className={cn(
                    "h-8 shrink-0 rounded-full px-3.5 text-[13px] font-semibold transition-colors duration-150 ease-out-strong",
                    isActive
                      ? "bg-text-primary text-surface dark:bg-white dark:text-black"
                      : "bg-surface-alt text-text-secondary dark:bg-white/[0.07] pointer-fine:hover:text-text-primary",
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {isLoading ? (
        <TileGridSkeleton />
      ) : !hasActivities ? (
        <EmptyState
          title="No activities yet"
          body="Activities show up here once they're added to Hiive."
        />
      ) : normalizedQuery ? (
        searchedItems.length > 0 ? (
          <PickerSection
            title="Results"
            hint={`${searchedItems.length} ${searchedItems.length === 1 ? "activity" : "activities"}`}
          >
            {renderTiles(searchedItems)}
          </PickerSection>
        ) : (
          <EmptyState
            title={`Nothing matches “${query.trim()}”`}
            body="Try another name, or browse every activity."
            action={
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setFilter("all");
                }}
                className="mt-4 text-sm font-semibold text-brand"
              >
                Browse all activities
              </button>
            }
          />
        )
      ) : (
        <>
          {filter === "popular" && recentItems.length > 0 ? (
            <PickerSection title="Jump back in">
              <div className="-mx-4 no-scrollbar flex gap-2 overflow-x-auto px-4">
                {recentItems.map((item) => (
                  <RecentChip
                    key={item.activity.id}
                    item={item}
                    onSelect={onSelectActivity}
                  />
                ))}
              </div>
            </PickerSection>
          ) : null}

          {filter === "popular" ? (
            <PickerSection title="Popular">
              {renderTiles(popularItems)}
              <button
                type="button"
                onClick={() => setFilter("all")}
                className="flex w-full items-center justify-between rounded-2xl px-1 py-3 text-sm font-semibold text-text-primary transition-colors pointer-fine:hover:text-brand"
              >
                Browse all {allItems.length} activities
                <ChevronRight className="size-4 text-text-tertiary" />
              </button>
            </PickerSection>
          ) : null}

          {filter === "matches" || filter === "all" ? (
            <PickerSection title="Matches" hint="Log a score after you play">
              {renderTiles(matchItems)}
            </PickerSection>
          ) : null}

          {filter === "sessions" || filter === "all" ? (
            <PickerSection title="Social" hint="No scores, just show up">
              {renderTiles(sessionItems)}
            </PickerSection>
          ) : null}
        </>
      )}
    </div>
  );
}
